import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Dropdown, Flex, Input, Menu, Skeleton } from 'antd';
import { AxiosResponse } from 'axios';
import { TbBuilding, TbExclamationCircle, TbSearch } from 'react-icons/tb';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WorkspaceResourceCollection, workspaceService } from '@/services/database';
import {
    CompanySearchResult,
    companySearchResultToSubjectReference,
    getCompanySearchResultExchange,
    getCompanySearchResultName,
    groupCompanySearchResults,
} from '@/shared/components/company-search/utils';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { useWorkspaceStore } from '@/stores';
import { SubjectReference, WorkspaceSubjectType } from '@/types';

import { WorkspaceResourceGlyph } from '../WorkspaceResourceAvatar';

type SelectableWorkspaceCollection = Exclude<WorkspaceResourceCollection, 'members'>;
type WorkspaceSubjectCollection = 'companies' | SelectableWorkspaceCollection;

interface WorkspaceSubjectDefinition {
    collection: WorkspaceSubjectCollection;
    description: string;
    icon: ReactNode;
    label: string;
    pluralLabel: string;
    subjectType: WorkspaceSubjectType;
}

interface WorkspaceSubjectOption {
    avatar: {
        icon?: ReactNode;
        listType?: string;
        resource?: string;
        website?: string;
    };
    metadata: ReactNode[];
    searchText: string;
    subject: SubjectReference;
    value: string;
}

interface WorkspaceSubjectSelectProps {
    disabled?: boolean;
    value: SubjectReference[];
    onChange: (subjects: SubjectReference[]) => void;
}

const WORKSPACE_SUBJECT_DEFINITIONS: WorkspaceSubjectDefinition[] = [
    {
        collection: 'companies',
        description: 'Public and private companies from global search.',
        icon: <TbBuilding size='1rem' />,
        label: 'Company',
        pluralLabel: 'Companies',
        subjectType: WorkspaceSubjectType.SEARCH_COMPANY,
    },
    {
        collection: 'investments',
        description: 'Companies and opportunities tracked by this workspace.',
        icon: <WorkspaceResourceGlyph resource='investment' size='1rem' />,
        label: 'Investment',
        pluralLabel: 'Investments',
        subjectType: WorkspaceSubjectType.INVESTMENT,
    },
    {
        collection: 'memos',
        description: 'Research notes, theses, and collaborative memos.',
        icon: <WorkspaceResourceGlyph resource='memo' size='1rem' />,
        label: 'Memo',
        pluralLabel: 'Memos',
        subjectType: WorkspaceSubjectType.MEMO,
    },
    {
        collection: 'folders',
        description: 'Workspace folders containing related diligence resources.',
        icon: <WorkspaceResourceGlyph resource='folder' size='1rem' />,
        label: 'Folder',
        pluralLabel: 'Folders',
        subjectType: WorkspaceSubjectType.FOLDER,
    },
    {
        collection: 'pipelines',
        description: 'Pipelines used to organize investment workflows.',
        icon: <WorkspaceResourceGlyph resource='pipeline' size='1rem' />,
        label: 'Pipeline',
        pluralLabel: 'Pipelines',
        subjectType: WorkspaceSubjectType.PIPELINE,
    },
    {
        collection: 'dashboards',
        description: 'Dashboards and reusable reporting surfaces.',
        icon: <WorkspaceResourceGlyph resource='dashboard' size='1rem' />,
        label: 'Dashboard',
        pluralLabel: 'Dashboards',
        subjectType: WorkspaceSubjectType.DASHBOARD,
    },
    {
        collection: 'lists',
        description: 'Relationship lists and structured operating data.',
        icon: <WorkspaceResourceGlyph resource='list' size='1rem' />,
        label: 'List',
        pluralLabel: 'Lists',
        subjectType: WorkspaceSubjectType.LIST,
    },
    {
        collection: 'teams',
        description: 'Workspace teams and their shared resources.',
        icon: <WorkspaceResourceGlyph resource='team' size='1rem' />,
        label: 'Team',
        pluralLabel: 'Teams',
        subjectType: WorkspaceSubjectType.TEAM,
    },
];

const subjectKey = ({ subjectId, subjectType }: Pick<SubjectReference, 'subjectId' | 'subjectType'>) =>
    `${subjectType}:${subjectId}`;

const firstString = (record: Record<string, unknown>, keys: string[]) =>
    keys.map((key) => record[key]).find((value): value is string => typeof value === 'string' && Boolean(value.trim()));

const resultWebsite = (result?: CompanySearchResult) =>
    result?.enrichment?.website ?? result?.website ?? result?.enrichment?.clearbit?.domain;

const normalizeWorkspaceOption = (
    resource: unknown,
    definition: WorkspaceSubjectDefinition,
): WorkspaceSubjectOption | undefined => {
    if (!resource || typeof resource !== 'object') {
        return undefined;
    }

    const record = resource as Record<string, unknown>;
    const id = record.id;

    if (typeof id !== 'string' && typeof id !== 'number') {
        return undefined;
    }

    const label =
        firstString(record, ['name', 'title', 'editedName', 'symbol', 'tickerSymbol']) ??
        `${definition.label} ${String(id)}`;
    const ticker = firstString(record, ['tickerSymbol', 'symbol']);
    const website = firstString(record, ['website']);
    const listType = firstString(record, ['type']);
    const subject: SubjectReference = {
        metadata: ticker ? { ticker } : undefined,
        subjectId: String(id),
        subjectLabel: label,
        subjectType: definition.subjectType,
    };

    return {
        avatar: {
            listType,
            resource: definition.label.toLowerCase(),
            website,
        },
        metadata: [definition.label, ticker],
        searchText: [label, ticker, website].filter(Boolean).join(' ').toLowerCase(),
        subject,
        value: subjectKey(subject),
    };
};

const normalizeCompanyOption = (result: CompanySearchResult): WorkspaceSubjectOption | undefined => {
    const subject = companySearchResultToSubjectReference(result);

    if (!subject) {
        return undefined;
    }

    const symbol = result.symbol ?? result.ticker ?? result.enrichment?.symbol;
    const exchange = getCompanySearchResultExchange(result);
    const website = resultWebsite(result);

    return {
        avatar: { icon: <TbBuilding size='0.875rem' />, website },
        metadata: [exchange !== 'Unknown Exchange' ? exchange : null, symbol],
        searchText: [getCompanySearchResultName(result), exchange, symbol, website]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        subject,
        value: subjectKey(subject),
    };
};

const WorkspaceSubjectSelect: FC<WorkspaceSubjectSelectProps> = ({ disabled, onChange, value }) => {
    const { token } = useAntDesignTheme();
    const workspaceId = useWorkspaceStore((state) => state.selectedWorkspace?.id);
    const requestId = useRef(0);
    const initialCollection =
        WORKSPACE_SUBJECT_DEFINITIONS.find((candidate) => candidate.subjectType === value[0]?.subjectType)
            ?.collection ?? 'companies';
    const [collection, setCollection] = useState<WorkspaceSubjectCollection>(initialCollection);
    const [companyResults, setCompanyResults] = useState<CompanySearchResult[]>([]);
    const [error, setError] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(initialCollection !== 'companies' && Boolean(workspaceId));
    const [workspaceResources, setWorkspaceResources] = useState<unknown[]>([]);
    const definition =
        WORKSPACE_SUBJECT_DEFINITIONS.find((candidate) => candidate.collection === collection) ??
        WORKSPACE_SUBJECT_DEFINITIONS[0];

    useEffect(() => {
        if (collection === 'companies' || !workspaceId) {
            return;
        }

        const activeRequestId = ++requestId.current;

        void workspaceService
            .getResourceCollection(workspaceId, collection)
            .then((resources) => {
                if (requestId.current === activeRequestId) {
                    setWorkspaceResources(resources ?? []);
                }
            })
            .catch((resourceError: unknown) => {
                captureException(resourceError);
                if (requestId.current === activeRequestId) {
                    setError(true);
                    setWorkspaceResources([]);
                }
            })
            .finally(() => {
                if (requestId.current === activeRequestId) {
                    setLoading(false);
                }
            });
    }, [collection, workspaceId]);

    useDebounce(
        () => {
            if (collection !== 'companies') {
                return;
            }

            const fragment = input.trim();

            if (fragment.length < 2) {
                requestId.current += 1;
                setCompanyResults([]);
                setError(false);
                setLoading(false);
                return;
            }

            const activeRequestId = ++requestId.current;
            setError(false);
            setLoading(true);

            void authorizedAxiosInstance
                .post<CompanySearchResult[]>('/api/search/vendors/fmp/companies', { fragment })
                .then((response: AxiosResponse<CompanySearchResult[]>) => {
                    if (requestId.current === activeRequestId) {
                        setCompanyResults(response.data ?? []);
                    }
                })
                .catch((searchError: unknown) => {
                    captureException(searchError);
                    if (requestId.current === activeRequestId) {
                        setCompanyResults([]);
                        setError(true);
                    }
                })
                .finally(() => {
                    if (requestId.current === activeRequestId) {
                        setLoading(false);
                    }
                });
        },
        450,
        [collection, input],
    );

    const selectedKeys = useMemo(() => new Set(value.map(subjectKey)), [value]);
    const options = useMemo(() => {
        const selectedOptions = value
            .filter((subject) => subject.subjectType === definition.subjectType)
            .map<WorkspaceSubjectOption>((subject) => ({
                avatar: {
                    icon: definition.collection === 'companies' ? <TbBuilding size='0.875rem' /> : undefined,
                    resource: definition.collection === 'companies' ? undefined : definition.label.toLowerCase(),
                },
                metadata: [definition.label],
                searchText: [subject.subjectLabel, subject.subjectId].filter(Boolean).join(' ').toLowerCase(),
                subject,
                value: subjectKey(subject),
            }));
        const resultOptions =
            collection === 'companies'
                ? groupCompanySearchResults(companyResults)
                      .flatMap((group) => [group.primary, ...group.alternatives])
                      .map(normalizeCompanyOption)
                      .filter((option): option is WorkspaceSubjectOption => Boolean(option))
                : workspaceResources
                      .map((resource) => normalizeWorkspaceOption(resource, definition))
                      .filter((option): option is WorkspaceSubjectOption => Boolean(option));
        const normalizedInput = input.trim().toLowerCase();

        return Array.from(
            new Map([...selectedOptions, ...resultOptions].map((option) => [option.value, option])).values(),
        ).filter((option) => !normalizedInput || option.searchText.includes(normalizedInput));
    }, [collection, companyResults, definition, input, value, workspaceResources]);

    const toggleSubject = (subject: SubjectReference) => {
        const key = subjectKey(subject);
        const nextValue = selectedKeys.has(key)
            ? value.filter((candidate) => subjectKey(candidate) !== key)
            : [...value, subject];

        onChange(nextValue);
    };

    const emptyState = (() => {
        if (error) {
            return {
                description: `The ${definition.pluralLabel.toLowerCase()} search is temporarily unavailable. Try again shortly.`,
                icon: <TbExclamationCircle size='1.25rem' />,
                label: `Unable to Load ${definition.pluralLabel}`,
            };
        }

        if (collection === 'companies' && input.trim().length < 2) {
            return {
                description: 'Type at least two characters to search public and private company listings.',
                icon: <TbSearch size='1.25rem' />,
                label: 'Search For Companies',
            };
        }

        return {
            description: `No ${definition.pluralLabel.toLowerCase()} match this search.`,
            icon: definition.icon,
            label: `No ${definition.pluralLabel} Found`,
        };
    })();

    return (
        <div
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '22rem',
                minHeight: '15rem',
                overflow: 'hidden',
            }}
        >
            <div style={{ borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: token.paddingSM }}>
                <Flex align='center' gap={token.marginXS}>
                    <Dropdown
                        disabled={disabled}
                        placement='bottomLeft'
                        trigger={['click']}
                        popupRender={() => (
                            <DropdownPanel width='20rem'>
                                <DropdownMenuLabel>Resource Type</DropdownMenuLabel>
                                <DropdownPanelMenu selectable={false}>
                                    {WORKSPACE_SUBJECT_DEFINITIONS.map((candidate) => (
                                        <Menu.Item
                                            key={candidate.collection}
                                            onClick={() => {
                                                requestId.current += 1;
                                                setError(candidate.collection !== 'companies' && !workspaceId);
                                                setLoading(
                                                    candidate.collection !== 'companies' && Boolean(workspaceId),
                                                );
                                                setCollection(candidate.collection);
                                                setCompanyResults([]);
                                                setInput('');
                                                setWorkspaceResources([]);
                                            }}
                                        >
                                            <DropdownOptionContent
                                                avatar={{ icon: candidate.icon }}
                                                description={candidate.description}
                                                selected={collection === candidate.collection}
                                                title={candidate.pluralLabel}
                                            />
                                        </Menu.Item>
                                    ))}
                                </DropdownPanelMenu>
                            </DropdownPanel>
                        )}
                    >
                        <DropdownButton disabled={disabled} style={{ flex: 'none' }}>
                            {definition.icon}
                            <span>{definition.pluralLabel}</span>
                        </DropdownButton>
                    </Dropdown>
                    <Input
                        allowClear
                        disabled={disabled}
                        placeholder={
                            collection === 'companies' ? 'Search Companies...' : `Filter ${definition.pluralLabel}...`
                        }
                        prefix={<TbSearch aria-hidden style={{ color: token.colorTextQuaternary }} />}
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                    />
                </Flex>
                <small style={{ color: token.colorTextTertiary, display: 'block', marginTop: token.marginXS }}>
                    {value.length.toLocaleString()} Resource{value.length === 1 ? '' : 's'} Selected
                </small>
            </div>
            <OverflowFadeScrollArea
                aria-label='Workspace subject results'
                containerStyle={{ flex: '1 1 auto', minHeight: 0 }}
                surface='container'
            >
                {loading ? (
                    <div style={{ padding: token.paddingSM }}>
                        <Skeleton active paragraph={{ rows: 3 }} title={false} />
                    </div>
                ) : options.length ? (
                    <DropdownPanelMenu selectable={false}>
                        {options.map((option) => (
                            <Menu.Item key={option.value} onClick={() => toggleSubject(option.subject)}>
                                <DropdownOptionContent
                                    avatar={option.avatar}
                                    metadata={option.metadata}
                                    selected={selectedKeys.has(option.value)}
                                    title={option.subject.subjectLabel ?? option.subject.subjectId}
                                />
                            </Menu.Item>
                        ))}
                    </DropdownPanelMenu>
                ) : (
                    <EmptyView
                        description={emptyState.description}
                        height='10rem'
                        icon={emptyState.icon}
                        label={emptyState.label}
                    />
                )}
            </OverflowFadeScrollArea>
        </div>
    );
};

export type { WorkspaceSubjectCollection, WorkspaceSubjectSelectProps };
export default WorkspaceSubjectSelect;
